import axios from "axios";
import {
  LOGIN_ADMIN,
  GET_ARTICLES,
  GET_USERS,
  GET_CATEGORIES,
  CREATE_CATEGORY,
  DELETE_CATEGORY,
  DELETE_ARTICLE,
  ENABLE_CATEGORY,
  TOGGLE_CATEGORY_STATUS,
  GET_SUBCATEGORIES,
  CREATE_SUBCATEGORY,
  TOGGLE_SUBCATEGORY_STATUS,
  DELETE_SUBCATEGORY,
  TOGGLE_WRITER_STATUS,
  GET_DASHBOARD,
  GET_BANNERS,
  GET_BANNER_CLICKS,
  GET_VISITORS,
  GET_VISITOR_SUMMARY,
  GET_VISITORS_CHART,
  GET_VISITOR_PAGES,
  CREATE_BANNER,
  TOGGLE_BANNER_STATUS,
  DELETE_BANNER,
} from "./endpoints";
import { getAuthToken } from "./auth";

const api = axios.create({
  baseURL: "https://api.media24hr.com/api/admin",
  // baseURL: "http://localhost:5000/api/admin",
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  },
);

async function get(url, params) {
  try {
    const { data } = await api.get(url, { params });
    return { isError: false, data, error: null };
  } catch (error) {
    return { isError: true, error, data: null };
  }
}

export const getArticles = async (params) => get(GET_ARTICLES, params);
export const getUsers = async () => get(GET_USERS);
export const getCategories = async () => get(GET_CATEGORIES);
export const getSubcategories = async (categoryUid) =>
  get(`${GET_SUBCATEGORIES}/${categoryUid}/subcategories`);
export const getDashboard = async () => get(GET_DASHBOARD);
export const getBanners = async () => get(GET_BANNERS);
export const getBannerClicks = async (uid, params) =>
  get(`${GET_BANNER_CLICKS}/${uid}/clicks`, params);
export const getVisitors = async (params) => get(GET_VISITORS, params);
export const getVisitorSummary = async () => get(GET_VISITOR_SUMMARY);
export const getVisitorsChart = async (params) => get(GET_VISITORS_CHART, params);
export const getVisitorPages = async (params) => get(GET_VISITOR_PAGES, params);
export const createCategory = (payload) => api.post(CREATE_CATEGORY, payload);
export const createSubcategory = (categoryUid, payload) =>
  api.post(`${CREATE_SUBCATEGORY}/${categoryUid}/subcategories`, payload);
export const createBanner = (payload) => api.post(CREATE_BANNER, payload);
export const deleteCategory = (id) => api.delete(`${DELETE_CATEGORY}/${id}`);
export const enableCategory = (uid) => api.put(`${ENABLE_CATEGORY}/${uid}/enable`);
export const toggleCategoryStatus = (uid) =>
  api.put(`${TOGGLE_CATEGORY_STATUS}/${uid}/toggle-status`);
export const deleteArticle = (uid) => api.delete(`${DELETE_ARTICLE}/${uid}`);
export const toggleSubcategoryStatus = (uid) =>
  api.put(`${TOGGLE_SUBCATEGORY_STATUS}/${uid}/toggle-status`);
export const toggleWriterStatus = (uid) =>
  api.put(`${TOGGLE_WRITER_STATUS}/${uid}/toggle-status`);
export const toggleBannerStatus = (uid) =>
  api.put(`${TOGGLE_BANNER_STATUS}/${uid}/toggle-status`);
export const deleteSubcategory = (uid) =>
  api.delete(`${DELETE_SUBCATEGORY}/${uid}`);
export const deleteBanner = (uid) => api.delete(`${DELETE_BANNER}/${uid}`);
export const loginAdmin = (payload) => api.post(LOGIN_ADMIN, payload);

async function compressImageFile(file) {
  if (typeof window === "undefined" || !file?.type?.startsWith("image/")) {
    return file;
  }

  const maxWidth = 1600;
  const maxHeight = 900;
  const quality = 0.82;

  if (file.size < 700 * 1024 && file.type !== "image/png") {
    return file;
  }

  const image = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const preview = new window.Image();
      preview.onload = () => resolve(preview);
      preview.onerror = reject;
      preview.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const canvas = document.createElement("canvas");
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));

  const context = canvas.getContext("2d");
  if (!context) return file;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const outputType = file.type === "image/png" ? "image/webp" : file.type || "image/jpeg";
  const blob = await new Promise((resolve) => {
    canvas.toBlob((result) => resolve(result), outputType, quality);
  });

  if (!blob) return file;

  const extension = outputType === "image/webp" ? "webp" : outputType === "image/png" ? "png" : "jpg";
  return new File([blob], file.name.replace(/\.[^.]+$/, `.${extension}`), {
    type: blob.type || outputType,
    lastModified: Date.now(),
  });
}

export async function uploadBannerImage(file) {
  const form = new FormData();
  try {
    form.append("image", await compressImageFile(file));
    // Keep the ads uploader aligned with the writer uploader. Axios adds the
    // multipart boundary automatically when Content-Type is not overridden.
    const response = await axios.post("https://upload.media24hr.com/upload", form, {
      timeout: 60000,
    });

    return {
      data: response.data,
      error: null,
      isError: false,
    };
  } catch (error) {
    return {
      data: null,
      error,
      isError: true,
    };
  }
}

export default api;
